import { Link } from "react-router-dom";
import { CardComp } from "../../../../components";
import { AdminNeedsAttentionGrouped } from "../../../../components/Admin/InvestorPortal/AdminNeedsAttentionCard";
import AdminModulePageShell from "../../../../components/Admin/InvestorPortal/AdminModulePageShell";
import ActivityFeed from "../../../../components/shared/ActivityFeed";
import { formatMoney } from "../../../../utils/currencyFormat";
import { RequestStatusBadge } from "../../../../components/Admin/InvestorPortal/AdminStatusBadge";
import {
  useGetStaffInvestmentDashboardSummaryQuery,
  useListStaffInvestmentRequestsQuery,
  useListStaffOpportunitiesQuery,
} from "../../../../store/admin/investorPortal/staffInvestmentsApi";
import { useGetStaffMarketplaceDashboardQuery } from "../../../../store/admin/marketplace/staffMarketplaceApi";
import { buildHubMarketplaceAttentionFromSummary } from "../../../../store/admin/marketplace/needsAttention";
import { useGetStaffRealEstateSummaryQuery } from "../../../../store/admin/investorOperations/staffRealEstateApi";
import { buildHubRealEstateAttentionFromSummary } from "../../../../store/admin/investorOperations/needsAttention";
import {
  mapStaffDashboardSummary,
  mapStaffInvestmentRequestListItem,
  mapStaffOpportunityListItem,
} from "../../../../utils/staffInvestmentsAdapter";
import moment from "moment";
import { withInvestorPortalGuard } from "../withInvestorPortalGuard";

function InvestorPortalDashboard() {
  const { data: apiSummaryRaw } = useGetStaffInvestmentDashboardSummaryQuery();
  const { data: pendingApi } = useListStaffInvestmentRequestsQuery({ status: "submitted", per_page: 5 });
  const { data: closingSoonApi } = useListStaffOpportunitiesQuery({ closing_soon: 1, per_page: 5 });
  const { data: marketplaceSummary } = useGetStaffMarketplaceDashboardQuery();
  const { data: realEstateSummary } = useGetStaffRealEstateSummaryQuery();

  const apiSummary = mapStaffDashboardSummary(apiSummaryRaw);
  const pendingItems = (pendingApi?.items || []).map(mapStaffInvestmentRequestListItem).filter(Boolean);
  const closingItems = (closingSoonApi?.items || []).map(mapStaffOpportunityListItem).filter(Boolean);
  const marketplaceAttention = buildHubMarketplaceAttentionFromSummary(marketplaceSummary || {});
  const realEstateAttention = buildHubRealEstateAttentionFromSummary(realEstateSummary || {});

  const metrics = {
    ...apiSummary,
    totalFundsRequested: apiSummary?.totalFundsRequested ?? 0,
    approvedInvestmentValue: apiSummary?.approvedInvestmentValue ?? 0,
    expectedReturnExposure: apiSummary?.expectedReturnExposure ?? 0,
    activeOpportunities: apiSummary?.activeOpportunities ?? 0,
    totalParticipants: apiSummary?.totalParticipants ?? 0,
    requestsAwaitingReview: apiSummary?.requestsAwaitingReview ?? 0,
    closingSoon: closingItems,
    upcomingMaturities: [],
    fundingOverview: (apiSummary?.fundingOverview || []).map((o) => ({
      ...o,
      title: o.title || "Portfolio",
      progressPct: o.fundingTargetNgn
        ? Math.round((o.fundedAmountNgn / o.fundingTargetNgn) * 100)
        : 0,
    })),
    pendingRequests: pendingItems,
    needsAttention: [
      ...(apiSummary?.needsAttention || []),
      ...marketplaceAttention,
      ...realEstateAttention,
    ],
  };

  const summaryCards = [
    { label: "Total funds requested", value: formatMoney(metrics.totalFundsRequested), hint: "All investment requests" },
    { label: "Approved investment value", value: formatMoney(metrics.approvedInvestmentValue), hint: "Approved & allocated" },
    { label: "Expected return exposure", value: formatMoney(Math.round(metrics.expectedReturnExposure)), hint: "Projected returns" },
    { label: "Active opportunities", value: String(metrics.activeOpportunities), hint: "Published & funding" },
    { label: "Investment participants", value: String(metrics.totalParticipants), hint: "Unique participants" },
    { label: "Closing soon", value: String(metrics.closingSoon.length), hint: "Within 14 days" },
    { label: "Awaiting review", value: String(metrics.requestsAwaitingReview), hint: "Submitted & under review" },
    { label: "Upcoming maturities", value: String(metrics.upcomingMaturities.length), hint: "Next payouts" },
  ];

  return (
    <AdminModulePageShell title="Investment Hub — Overview" className="investor-portal-dashboard">
      <CardComp cardClassName="border-0 shadow-sm mb-4" data-tour="staff-invest-hub-attention">
        <h6 className="investor-portal-section-title">Needs attention</h6>
        <p className="text-muted small mb-3">What needs your attention today?</p>
        <AdminNeedsAttentionGrouped items={metrics.needsAttention || []} />
      </CardComp>

      <div className="row g-3 mb-4" data-tour="staff-invest-hub-cards">
        {summaryCards.map((c) => (
          <div key={c.label} className="col-6 col-md-4 col-xl-3">
            <CardComp cardClassName="border-0 shadow-sm h-100 metric-card">
              <p className="text-muted small mb-2">{c.label}</p>
              <h5 className="fw-bold mb-2" style={{ color: "#383838" }}>
                {c.value}
              </h5>
              <p className="text-muted small mb-0">{c.hint}</p>
            </CardComp>
          </div>
        ))}
      </div>

      <div className="row g-4">
        <div className="col-lg-6">
          <CardComp cardClassName="border-0 shadow-sm h-100">
            <h6 className="investor-portal-section-title">Funding overview</h6>
            {metrics.fundingOverview.length === 0 ? (
              <p className="text-muted small mb-0">No active opportunities.</p>
            ) : (
              <div className="d-flex flex-column gap-3">
                {metrics.fundingOverview.map((o) => (
                  <div key={o.id}>
                    <div className="d-flex justify-content-between align-items-start gap-2 mb-1">
                      <Link
                        to={`/super_admin/investor_portal/investments/opportunities/${o.id}`}
                        className="fw-semibold small text-decoration-none flex-grow-1"
                        style={{ color: "var(--color-secondary)" }}
                      >
                        {o.title}
                      </Link>
                      <span className="small fw-bold flex-shrink-0">{o.progressPct}%</span>
                    </div>
                    <div className="progress rounded-pill" style={{ height: 8 }}>
                      <div
                        className="progress-bar"
                        style={{ width: `${o.progressPct}%`, backgroundColor: "var(--color-primary)" }}
                      />
                    </div>
                    <p className="text-muted small mb-0 mt-1">
                      {formatMoney(o.fundedAmountNgn)} of {formatMoney(o.fundingTargetNgn)}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </CardComp>
        </div>

        <div className="col-lg-6">
          <CardComp cardClassName="border-0 shadow-sm h-100">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h6 className="investor-portal-section-title mb-0">Requests requiring attention</h6>
              <Link
                to="/super_admin/investor_portal/investments/requests"
                className="small fw-semibold text-decoration-none"
                style={{ color: "var(--color-secondary)" }}
              >
                View all
              </Link>
            </div>
            {metrics.pendingRequests.length === 0 ? (
              <p className="text-muted small mb-0">No pending requests.</p>
            ) : (
              <ul className="list-unstyled mb-0">
                {metrics.pendingRequests.map((r) => (
                  <li key={r.id} className="py-2 border-bottom">
                    <div className="d-flex justify-content-between gap-2 flex-wrap">
                      <div>
                        <Link
                          to={`/super_admin/investor_portal/investments/requests/${r.id}`}
                          className="fw-semibold small text-decoration-none"
                          style={{ color: "#383838" }}
                        >
                          {r.participantName}
                        </Link>
                        <p className="text-muted small mb-0">{r.opportunityTitle}</p>
                      </div>
                      <div className="text-end">
                        <RequestStatusBadge status={r.status} />
                        <p className="small mb-0 mt-1">{formatMoney(r.totalNgn)}</p>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </CardComp>
        </div>

        <div className="col-lg-6">
          <CardComp cardClassName="border-0 shadow-sm h-100">
            <h6 className="investor-portal-section-title">Opportunities closing soon</h6>
            {metrics.closingSoon.length === 0 ? (
              <p className="text-muted small mb-0 py-2">None closing in the next 14 days.</p>
            ) : (
              <div className="table-responsive">
                <table className="investor-portal-mini-table mb-0">
                  <thead>
                    <tr>
                      <th>Opportunity</th>
                      <th>Closes</th>
                      <th>Progress</th>
                      <th>Units left</th>
                    </tr>
                  </thead>
                  <tbody>
                    {metrics.closingSoon.map((o) => (
                      <tr key={o.id}>
                        <td>{o.title}</td>
                        <td>{moment(o.fundingCloseDate).format("DD-MMM-YYYY")}</td>
                        <td>
                          {o.fundingTargetNgn ? Math.round((o.fundedAmountNgn / o.fundingTargetNgn) * 100) : 0}%
                        </td>
                        <td>{o.availableUnits}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardComp>
        </div>

        <div className="col-lg-6">
          <CardComp cardClassName="border-0 shadow-sm h-100">
            <h6 className="investor-portal-section-title">Upcoming maturities</h6>
            <p className="text-muted small mb-0 py-2">No maturities scheduled.</p>
          </CardComp>
        </div>

        <div className="col-12">
          <CardComp cardClassName="border-0 shadow-sm">
            <h6 className="investor-portal-section-title">Recent activity</h6>
            <ActivityFeed
              guard="staff"
              perPage={10}
              showFilters
              showPagination={false}
              groupByDate
              compact
              emptyMessage="No recent activity."
            />
          </CardComp>
        </div>
      </div>
    </AdminModulePageShell>
  );
}

export default withInvestorPortalGuard(InvestorPortalDashboard, "investorPortal");
